import { useState, useEffect, useCallback, useRef } from 'react';
import { Platform, View, StyleSheet, Dimensions, ScrollView, useWindowDimensions, ActivityIndicator, Linking } from 'react-native';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useUser } from '../../src/hooks/useUser';
import { useVehicles } from '../../src/hooks/useVehicles';
import { useTimeLogs } from '../../src/hooks/useTimeLogs';
import { useEndSession } from '../../src/hooks/useEndSession';
import { useLocationTracking } from '../../src/hooks/useLocationTracking';
import { subscribeBackgroundAlertReconciliation } from './backgroundAlertReconciliation';
import { useForegroundLocation } from '../../src/hooks/useForegroundLocation';
import { useProfile } from '../../src/hooks/useProfile';
import { usePaywallStatus } from '../../src/hooks/usePaywallStatus';
import { Sidebar, SIDEBAR_WIDTH } from '../../src/components/Sidebar';
import { EmploymentModal } from '../../src/components/EmploymentModal';
import { VehicleModal } from '../../src/components/VehicleModal';
import { SessionModal } from '../../src/components/SessionModal';
import { PendingWarningModal } from '../../src/components/PendingWarningModal';
import { OdometerVelocityModal } from '../../src/components/OdometerVelocityModal';
import { OdometerCorrectionModal } from '../../src/components/OdometerCorrectionModal';
import { DashboardContent } from '../../src/components/DashboardContent';
import { OwnerDashboardContent } from '../../src/components/OwnerDashboardContent';
import { FORM_DEFAULTS, POLL_INTERVAL } from '../../src/lib/constants';
import { companySchema, vehicleSchema, odometerSchema,  OdometerFormData } from '../../src/lib/types';
import { Portal, Dialog, Text, Button, useTheme,  Surface } from 'react-native-paper';
import { useRouter, useLocalSearchParams, Redirect } from 'expo-router';
import AsyncStorage from '@react-native-async-storage/async-storage';
import * as Notifications from 'expo-notifications';
import * as Location from 'expo-location';
import { API_BASE, API_ENDPOINTS, APP_ORIGIN } from '../../src/api/config';
import { apiFetch, createSelfPayBillingPortalSession, endSession, getUserDetails, getCompanyLicenses, getCompanyStatistics, correctOdometer, startSession, activateOwnerDriverProfile } from '../../src/api/apiService';
import type { CompanyLicenses, OdometerVelocityWarning, DriverProfileBoundaryResponse } from '../../src/api/apiService';
import { getLocationString, LOCATION_ERROR } from '../../src/utils/locationUtils';
import { getOnboardingRedirect, shouldRedirectToOwnerOnboarding } from '../../src/utils/onboardingRedirect';
import { usePartnerHomeRoute } from '../../src/utils/partnerHome';
import { deriveSelfPayEntitlement } from '../../src/utils/selfPayEntitlement';
import { shouldShowEmploymentModal } from '../../src/utils/driverEmploymentGate';
import { track } from '../../src/lib/analytics';
import { useTranslation } from 'react-i18next';
import { useColorScheme } from 'react-native';
import { useCompanyContext } from '../../src/context/CompanyContext';
import { UnifiedBuyLicensesModal } from '../../src/components/UnifiedBuyLicensesModal';
import { InviteDriverModal } from '../../src/components/InviteDriverModal';
import { DriverProfileRecoveryState } from '../../src/components/DriverProfileRecoveryState';
import { CompleteDriverProfileModal } from '../../src/components/CompleteDriverProfileModal';
import i18n from '../../src/lib/i18n';
import { LoadingScreen } from '../../src/components/LoadingScreen';
import { handlePurchaseParams } from '@features/dashboard/purchaseHandlers';
import { LicenseProvider, useLicense } from '../../src/context/LicenseContext';
import { DriverHomeScreen } from '../../src/features/driver';
import { OwnerHomeScreen } from '../../src/features/owner';
import { isDriverHomeScreenEnabled, isOwnerHomeScreenEnabled } from '../../src/utils/featureToggles';
import {
  clearOwnerDriverStorage,
  isOwnerDriverProfileComplete,
} from '../../src/features/owner/ownerDriverPrompt';
import {
  deriveOwnerBecomeDriverSeatState,
  resolveAvailableCompanySeats,
} from '../../src/features/owner/ownerBecomeDriver';
import { useOwnerSeatRefresh } from '../../src/features/owner/useOwnerSeatRefresh';
import * as Sentry from '@sentry/react-native';
import {
  DRIVING_STATUS_NOTIFICATION_CATEGORY,
  DRIVING_STATUS_NOTIFICATION_ID,
  START_DRIVING_ACTION_ID,
  STOP_DRIVING_ACTION_ID,
  isDrivingStatusNotificationResponse,
} from '../../src/lib/drivingNotifications';


// Obtém as dimensões iniciais da tela
const { width: SCREEN_WIDTH } = Dimensions.get('window');

interface SessionResultObject {
  status: string;
  warning?: any;
  location?: string;
  previousSessionOut?: { id: number; odometer: number; timestamp: string };
  newOdometer?: number;
}

function isSessionResultObject(value: unknown): value is SessionResultObject {
  return typeof value === 'object' && value !== null && 'status' in value;
}




// Driver location map removed from dashboard (kept only for GPS page)

// Configuração inicial da notificação (only on native)
const setupNotificationHandler = () => {
  if (Platform.OS === 'web') return;
  Notifications.setNotificationHandler({
    handleNotification: async () => ({
      shouldShowBanner: true,
      shouldShowList: true,
      shouldPlaySound: false, // Disable sound by default
      shouldSetBadge: false,
    }),
  });
};

// Configuração do canal de notificação para status de condução (only on native)
const setupNotificationChannel = async () => {
  if (Platform.OS === 'web') {
    return;
  }
  await Notifications.setNotificationChannelAsync('driving-channel', {
    name: i18n.t('notifications.drivingStatus', 'Driving Status'),
    importance: Notifications.AndroidImportance.HIGH,
    enableVibrate: false,
  });
};

  // Função para atualizar a notificação persistente (only on native)
  const updateDrivingNotification = async (isDriving: boolean) => {
    if (Platform.OS === 'web') {
      return;
    }

    const actions = isDriving
      ? [{ identifier: STOP_DRIVING_ACTION_ID, buttonTitle: i18n.t('notifications.stopDrivingButton', 'Stop Driving') }]
      : [{ identifier: START_DRIVING_ACTION_ID, buttonTitle: i18n.t('notifications.startDrivingButton', 'Start Driving') }];

    // Remove a notificação anterior para evitar duplicatas
    await Notifications.dismissNotificationAsync(DRIVING_STATUS_NOTIFICATION_ID);

    await Notifications.setNotificationCategoryAsync(DRIVING_STATUS_NOTIFICATION_CATEGORY, actions);

    await Notifications.scheduleNotificationAsync({
      content: {
        title: isDriving ? i18n.t('notifications.driving', 'Driving') : i18n.t('notifications.activeSession', 'Active Session'),
        body: i18n.t('notifications.tapToChange', 'Tap to change driving state'),
        sticky: true, // Mantém a notificação persistente no Android
        categoryIdentifier: DRIVING_STATUS_NOTIFICATION_CATEGORY,
        data: { isDriving },
        sound: false, // Explicitly disable sound for persistent notification
      },
      trigger: null,
      identifier: DRIVING_STATUS_NOTIFICATION_ID,
    });

  };

// Wrapper component for owner dashboard that uses LicenseContext
function OwnerDashboardWrapper({
  userData,
  companyStats,
  desktopSidebarVisible,
  sidebarOpen,
  setSidebarOpen,
  width,
  isLargeScreen,
  handleLogout,
  hasDriver,
  effectiveProfile,
  updateProfile,
  router,
  theme,
  buyLicensesModalVisible,
  setBuyLicensesModalVisible,
  inviteModalVisible,
  setInviteModalVisible,
  handleLicensePurchaseSuccessBase,
  setAlertMessage,
  setAlertModalVisible,
  t,
  ownerEverHadLicenses, // Multi-company license check result
  isCheckingAllCompaniesForLicenses, // Multi-company check loading state
  safeRedirect,
  completeDriverProfileModalVisible,
  setCompleteDriverProfileModalVisible,
  onCompleteDriverProfileSuccess,
  onBecomeDriver,

}: {
  userData: any;
  companyStats: any;
  desktopSidebarVisible: boolean;
  sidebarOpen: boolean;
  setSidebarOpen: (open: boolean) => void;
  width: number;
  isLargeScreen: boolean;
  handleLogout: () => Promise<void>;
  hasDriver: boolean;
  effectiveProfile: 'Owner' | 'Driver';
  updateProfile: (profile: 'Owner' | 'Driver') => void;
  router: any;
  theme: any;
  buyLicensesModalVisible: boolean;
  setBuyLicensesModalVisible: (visible: boolean) => void;
  inviteModalVisible: boolean;
  setInviteModalVisible: (visible: boolean) => void;
  handleLicensePurchaseSuccessBase: (refreshLicenses: () => Promise<void>) => Promise<void>;
  setAlertMessage: (message: string) => void;
  setAlertModalVisible: (visible: boolean) => void;
  t: any;
  ownerEverHadLicenses: boolean | null;
  isCheckingAllCompaniesForLicenses: boolean;
  safeRedirect: (route: string) => void;
  completeDriverProfileModalVisible: boolean;
  setCompleteDriverProfileModalVisible: (visible: boolean) => void;
  onCompleteDriverProfileSuccess: (result?: DriverProfileBoundaryResponse) => void | Promise<void>;
  onBecomeDriver: () => void | Promise<void>;

}) {
  const { licenseInfo, isLoading: isLicenseLoading, error: licenseError, refreshLicenses } = useLicense();
  const hasOwnerDriverProfile = isOwnerDriverProfileComplete(userData?.profile);

  // D-046 (2026-10-11): derive the owner become-driver presentation from the
  // already-loaded licence state of the selected company. Unknown/loading/error
  // must never be presented as sold out.
  const becomeDriverSeatState = deriveOwnerBecomeDriverSeatState({
    hasLicenseInfo: licenseInfo !== null,
    isLoading: isLicenseLoading,
    hasError: Boolean(licenseError),
    availableSeats: resolveAvailableCompanySeats(licenseInfo),
  });

  useOwnerSeatRefresh(!hasDriver && becomeDriverSeatState === 'full', refreshLicenses);

  // The become-driver action (form or saved-profile reuse) can consume a seat,
  // so refresh the licence state afterwards to let the card flip automatically.
  const handleBecomeDriverAction = useCallback(async () => {
    await onBecomeDriver();
    // Opening a fresh form consumes no seat. Refreshing here would hide and
    // remount the modal during input; only saved-profile activation needs it.
    if (hasOwnerDriverProfile) await refreshLicenses();
  }, [onBecomeDriver, refreshLicenses, hasOwnerDriverProfile]);

  const handleCompleteDriverProfileSuccessWithRefresh = useCallback(
    async (result?: DriverProfileBoundaryResponse) => {
      await onCompleteDriverProfileSuccess(result);
      await refreshLicenses();
    },
    [onCompleteDriverProfileSuccess, refreshLicenses],
  );

  // Dashboard entry uses the same server-authoritative state as login. License
  // history and local flags are presentation signals only and cannot advance a
  // user past an incomplete onboarding state.
  useEffect(() => {
    if (isCheckingAllCompaniesForLicenses || !userData) return;
    let cancelled = false;
    void getOnboardingRedirect({ userData }).then((result) => {
      if (!cancelled && result.route !== '/dashboard/home') {
        safeRedirect(result.route || '/onboarding');
      }
    });
    return () => {
      cancelled = true;
    };
  }, [isCheckingAllCompaniesForLicenses, safeRedirect, userData]);

  const handleLicensePurchaseSuccess = () => {
    return handleLicensePurchaseSuccessBase(refreshLicenses);
  };

  return (
    <View style={styles.container}>
      {desktopSidebarVisible && (
        <Sidebar
          isOpen={true}
          onClose={() => {}} // Not needed for large screens
          screenWidth={width}
          onLogout={handleLogout}
          isOwner={true}
          isDriver={hasDriver}
          selectedProfile={effectiveProfile}
          onSwitchProfile={hasDriver ? (newProfile: 'Owner' | 'Driver') => { updateProfile(newProfile); } : undefined}
          onRegisterDriver={!hasDriver ? () => router.push('/register/driver?skipLogin=true') : undefined}
          onCompleteDriverProfile={!hasDriver && becomeDriverSeatState === 'available' ? () => { void handleBecomeDriverAction(); } : undefined}
          showBecomeDriver={!hasDriver && becomeDriverSeatState === 'available'}
          />
      )}
      
      <Surface style={{ flex: 1, backgroundColor: theme.colors.background, ...(desktopSidebarVisible ? { marginLeft: SIDEBAR_WIDTH } : {}) }}>
        <OwnerDashboardContent
          userData={userData || { company: null }}
          licenseInfo={licenseInfo}
          companyStats={companyStats}
          isLoading={isLicenseLoading}
          onOpenSidebar={() => setSidebarOpen(true)}
          onBuyLicense={() => setBuyLicensesModalVisible(true)}
          onBuyGpsLicense={() => setBuyLicensesModalVisible(true)}
          onInviteDriver={() => setInviteModalVisible(true)}
          becomeDriverSeatState={!hasDriver ? becomeDriverSeatState : 'unknown'}
          onBecomeDriver={!hasDriver ? () => { void handleBecomeDriverAction(); } : undefined}
        />

        {!isLargeScreen && (
          <Sidebar
            isOpen={sidebarOpen}
            onClose={() => setSidebarOpen(false)}
            screenWidth={width}
            onLogout={handleLogout}
            isOwner={true}
            isDriver={hasDriver}
            selectedProfile={effectiveProfile}
            onSwitchProfile={hasDriver ? (newProfile: 'Owner' | 'Driver') => { updateProfile(newProfile); } : undefined}
            onRegisterDriver={!hasDriver ? () => router.push('/register/driver?skipLogin=true') : undefined}
            onCompleteDriverProfile={!hasDriver && becomeDriverSeatState === 'available' ? () => { void handleBecomeDriverAction(); } : undefined}
            showBecomeDriver={!hasDriver && becomeDriverSeatState === 'available'}
          />
        )}

        <UnifiedBuyLicensesModal
          visible={buyLicensesModalVisible}
          onDismiss={() => setBuyLicensesModalVisible(false)}
          onSuccess={handleLicensePurchaseSuccess}
        />
        <InviteDriverModal
          visible={inviteModalVisible}
          onDismiss={() => setInviteModalVisible(false)}
          onSuccess={() => {
            setAlertMessage(t('ownerDashboard.inviteSuccess', 'Invitation sent successfully'));
            setAlertModalVisible(true);
          }}
        />
        <CompleteDriverProfileModal
          visible={completeDriverProfileModalVisible}
          onDismiss={() => setCompleteDriverProfileModalVisible(false)}
          onSuccess={handleCompleteDriverProfileSuccessWithRefresh}
        />
      </Surface>
    </View>
  );
}

export default function DashboardHome() {
  const { employments, hasActiveEmployment, modalVisible, setModalVisible, fromPendingWarning, setFromPendingWarning, fetchUserData, handleAddCompany, handleLogout, userData } = useUser();
  const router = useRouter();
  // WS7 (#207): the self-pay driver is entitled by their OWN license, so the
  // employer activating the employment is not what unblocks the app. Only ask
  // the server when there is no active employment — the company-pay path never
  // needs this call.
  const { status: paywallStatus, isUnknown: paywallUnknown } = usePaywallStatus({ enabled: hasActiveEmployment === false });
  // Tri-state: `null` while the entitlement is still hydrating (status === null /
  // isUnknown). We must NOT collapse an unknown entitlement into `false` — that
  // was the root cause of the self-pay dashboard bouncing back to /onboarding
  // (jobs 884/887). Only `true` (trialing/active + is_self_pay) means entitled.
  // This tri-state value is consumed ONLY by the onboarding redirect decision.
  // A company-funded (company-pay) subscription can also resolve to
  // active/trialing, so state alone is NOT enough: the entitlement only belongs
  // to the driver when the backend says is_self_pay === true. Otherwise this is
  // company-pay and the employment gate (not the license) decides.
  // #379: while `hasActiveEmployment` is still null the paywall hook is disabled
  // (`enabled === false`), so it reports isUnknown=false with status=null — which
  // would derive a CONCLUSIVE "no entitlement" before the status was ever asked
  // for. Treat "user data not hydrated yet" as UNKNOWN so the redirect effect
  // holds instead of bouncing an entitled self-pay driver to /onboarding
  // (job 15917386343, second reload).
  const hasSelfPayLicenseTriState: boolean | null = hasActiveEmployment === null
    ? null
    : deriveSelfPayEntitlement(paywallStatus, paywallUnknown);
  // Legacy consumers (useVehicles, DriverHomeScreen) expect a plain boolean;
  // an unknown entitlement degrades to `false` for *display/vehicle* purposes
  // but must NOT drive the redirect (which uses the tri-state above).
  const hasSelfPayLicense: boolean = hasSelfPayLicenseTriState ?? false;
  // WS7 (#207): a self-pay driver must never see the "Adicionar Informações
  // da Empresa" modal (company-funded onboarding). useUser sets `modalVisible`
  // from employments.length alone. Neutralize it whenever the entitlement is
  // resolved self-pay (true) OR still unknown (null, hydration) — the modal
  // must stay closed while we cannot yet tell a self-pay driver apart. Only a
  // conclusively company-funded driver (false) keeps the legacy modal behavior
  // (jobs 15920979476/79 + tri-state hydration fix).
  const employmentModalVisible: boolean =
    hasSelfPayLicenseTriState === null || hasSelfPayLicenseTriState === true
      ? false
      : (modalVisible ?? false);
  const paymentIssue = !!paywallStatus?.payment_action_required
    && paywallStatus.is_self_pay === true;
  // The employment gate belongs to the company-funded path. A driver with a
  // license of their own operates while the declared employment stays
  // informative (it is what puts them in the owner's report).
  const canOperateAsDriver = hasActiveEmployment === true || hasSelfPayLicense;
  // #207: a saída do ecrã de ativação pendente — pagar a própria licença.
  const goToSelfPay = useCallback(() => {
    track('selfpay_offer_taken', { user_role: 'driver', source: 'pending_activation' });
    router.replace('/dashboard/self-pay');
  }, [router]);
  const fixSelfPayPayment = useCallback(async () => {
    const frontendOrigin = Platform.OS === 'web' && typeof window !== 'undefined'
      ? window.location.origin
      : APP_ORIGIN;
    const response = await createSelfPayBillingPortalSession(
      `${frontendOrigin}/dashboard/home`,
      `${API_BASE}/api/subscriptions/billing_portal/`,
      Platform.OS !== 'web',
    );
    if (!response.ok || !response.data?.url) {
      throw new Error(response.errorMessage || 'Unable to open billing portal');
    }
    await Linking.openURL(response.data.url);
  }, []);
  const { hasVehicles, vehicles, selectedVehicle, setSelectedVehicle, vehicleModalVisible, setVehicleModalVisible, localRegNumber, setLocalRegNumber, fetchVehicleData, handleAddVehicle, formatRegistrationNumber, isLoading } = useVehicles({ hasActiveEmployment, hasSelfPayLicense });
  const { companies, isLoading: isCompaniesLoading, hasLoadedOnce: hasCompaniesLoadedOnce, selectedCompanyId } = useCompanyContext();
  
  // Get window dimensions and calculate screen sizes first
  const { width, height } = useWindowDimensions();
  const isLargeScreen = width > 1024;
  const isTablet = width >= 768 && width <= 1024; // iPad-sized screens
  
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [desktopSidebarVisible, setDesktopSidebarVisible] = useState(isLargeScreen);
  const [startSessionModalVisible, setStartSessionModalVisible] = useState(false);
  const [endSessionModalVisible, setEndSessionModalVisible] = useState(false);
  const [alertModalVisible, setAlertModalVisible] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [alertAction, setAlertAction] = useState<(() => void | Promise<void>) | null>(null);
  const [isStartingSession, setIsStartingSession] = useState(false);
  const [isVelocityConfirming, setIsVelocityConfirming] = useState(false);
  const [latestOdometer, setLatestOdometer] = useState<number | null>(null);
  const [locationPermissionError, setLocationPermissionError] = useState(false);
  const [locationSettingsError, setLocationSettingsError] = useState(false);
  // Odometer validation modals state
  const [velocityWarningVisible, setVelocityWarningVisible] = useState(false);
  const [velocityWarningData, setVelocityWarningData] = useState<OdometerVelocityWarning | null>(null);
  const [velocityWarningContext, setVelocityWarningContext] = useState<'start' | 'end' | null>(null);
  const [pendingSessionData, setPendingSessionData] = useState<OdometerFormData | null>(null);
  const [pendingLocationWkt, setPendingLocationWkt] = useState<string | null>(null);
  const [correctionModalVisible, setCorrectionModalVisible] = useState(false);
  const [correctionContext, setCorrectionContext] = useState<'start' | 'end' | null>(null);
  const [previousSessionOut, setPreviousSessionOut] = useState<{ id: number; odometer: number; timestamp: string } | null>(null);
  const [newOdometerValue, setNewOdometerValue] = useState<number | null>(null);
  const [isCorrectingOdometer, setIsCorrectingOdometer] = useState(false);
  // Keep multi-company license checking state (for cross-company checks)
  const [ownerEverHadLicenses, setOwnerEverHadLicenses] = useState<boolean | null>(null);
  const [companyStats, setCompanyStats] = useState<{ currently_driving: number; avg_drive_time_hours: number; avg_distance_km: number } | null>(null);
  const hasCheckedLicensesRef = useRef<boolean>(false); // Track if we've already checked licenses (only for login redirect)
  // Note: licenseCheckFlagRef is defined in OwnerDashboardWrapper where it's used
  const licenseCheckFlagRef = useRef<boolean | null>(null); // Track if flag was found (null = not checked yet, true = flag exists, false = no flag)
  // Removed driverLocations/currentlyDrivingCount from dashboard
  const colorScheme = useColorScheme();
  const userInterfaceStyle = colorScheme === 'dark' ? 'dark' : 'light';
  const [buyLicensesModalVisible, setBuyLicensesModalVisible] = useState(false);
  const [inviteModalVisible, setInviteModalVisible] = useState(false);
  const [completeDriverProfileModalVisible, setCompleteDriverProfileModalVisible] = useState(false);
  const [isRedirectingToOnboarding, setIsRedirectingToOnboarding] = useState(false);
  const [hasInitializedCache, setHasInitializedCache] = useState(false);
  const [initializationTimeout, setInitializationTimeout] = useState(false);
  // Keep state for multi-company license checking
  const [isCheckingAllCompaniesForLicenses, setIsCheckingAllCompaniesForLicenses] = useState(false);
  // Use the new profile hook with proper null checking
  const { selectedProfile, effectiveProfile, isLoading: profileLoading, updateProfile, hasOwner, hasDriver } = useProfile(userData?.groups || []);


  // Add safety check - don't render profile-dependent logic until userData is loaded
  const isUserDataLoaded = userData !== null;
  // A10b-1: this screen only renders Owner and Driver; a partner-only account goes to its portal.
  const partnerHome = usePartnerHomeRoute(userData);

  // Use LicenseContext for owner profile
  const isOwnerProfile = effectiveProfile === 'Owner';
  // Use selectedCompanyId from context if available, otherwise fall back to userData?.company
  const shouldFetchLicenses = isOwnerProfile && !!(selectedCompanyId || (userData?.company && userData?.groups?.includes('Owner')));
  const isDriverProfile = effectiveProfile === 'Driver';
  const hasOwnerDriverProfile = isOwnerDriverProfileComplete(userData?.profile);

  // The approved card is the explicit entry into the form. Restored registration
  // intent must not auto-open it when new capacity arrives.

  const theme = useTheme();
  const { t } = useTranslation();

  const handleCompleteDriverProfileSuccess = useCallback(async (result?: DriverProfileBoundaryResponse) => {
    await clearOwnerDriverStorage();
    await fetchUserData(true);
    // D-046 (2026-10-11): submission saves the profile and activates through
    // the existing backend boundary. There is no success alert and no purchase
    // question — the capacity card reflects the refreshed seat state. Only the
    // payer/relationship protections (D-009/D-013) still surface an
    // informational alert.
    if (result?.personal_payer_active) {
      setAlertAction(null);
      setAlertMessage(t('ownerDashboard.personalPayerActiveAlert', 'O seu perfil de condutor foi guardado. O seu plano pessoal ativo mantém o pagamento atual até ao fim do período pago.'));
      setAlertModalVisible(true);
    } else if (result?.relationship_conflict) {
      setAlertAction(null);
      setAlertMessage(t('ownerDashboard.relationshipConflictAlert', 'O seu perfil de condutor foi guardado. Resolva o vínculo atual com outra empresa antes de ativar aqui.'));
      setAlertModalVisible(true);
    }
  }, [fetchUserData, t, setAlertMessage, setAlertAction, setAlertModalVisible]);

  const handleActivateDriverAccess = useCallback(async () => {
    // Explicit D-046 activation/reuse for an already-saved complete profile.
    // Idempotent; never charges, starts a trial or switches payer, and reports
    // no success alert (the refreshed capacity card already reflects it).
    const response = await activateOwnerDriverProfile();
    await fetchUserData(true);
    const data = response.ok ? (response.data as DriverProfileBoundaryResponse | undefined) : undefined;
    if (data?.personal_payer_active) {
      setAlertAction(null);
      setAlertMessage(t('ownerDashboard.personalPayerActiveAlert', 'O seu perfil de condutor foi guardado. O seu plano pessoal ativo mantém o pagamento atual até ao fim do período pago.'));
      setAlertModalVisible(true);
    } else if (data?.relationship_conflict) {
      setAlertAction(null);
      setAlertMessage(t('ownerDashboard.relationshipConflictAlert', 'O seu perfil de condutor foi guardado. Resolva o vínculo atual com outra empresa antes de ativar aqui.'));
      setAlertModalVisible(true);
    } else if (!response.ok) {
      setAlertAction(null);
      setAlertMessage(t('ownerDashboard.activationFailedAlert', 'Não foi possível ativar o acesso de condutor. Tente novamente.'));
      setAlertModalVisible(true);
    }
    // driver_activated -> silent; license_required -> the sold-out card is the
    // next action (no purchase question).
  }, [fetchUserData, t, setAlertMessage, setAlertAction, setAlertModalVisible]);

  const handleOwnerBecomeDriver = useCallback(async () => {
    // D-046 (2026-10-11): one explicit become-driver action. An owner with an
    // already-saved complete profile reuses it through the existing
    // activateOwnerDriverProfile boundary; otherwise the profile form opens.
    if (hasOwnerDriverProfile) {
      await handleActivateDriverAccess();
    } else {
      setCompleteDriverProfileModalVisible(true);
    }
  }, [hasOwnerDriverProfile, handleActivateDriverAccess]);

  // Circuit breaker: prevent multiple redirects in same render cycle
  const redirectInProgressRef = useRef(false);
  const safeRedirect = useCallback((route: string) => {
    if (redirectInProgressRef.current) return;
    redirectInProgressRef.current = true;
    router.replace(route as Parameters<typeof router.replace>[0]);
  }, [router]);
  
  const reloadApp = useCallback(async () => {
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      window.location.reload();
      return;
    }

    try {
      const Updates = await import('expo-updates');
      if (Updates && 'reloadAsync' in Updates && typeof (Updates as any).reloadAsync === 'function') {
        await (Updates as any).reloadAsync();
        return;
      }
    } catch (error) {
      // Ignore if expo-updates isn't available
    }

    try {
      const { DevSettings } = await import('react-native');
      if (DevSettings && typeof DevSettings.reload === 'function') {
        DevSettings.reload();
      }
    } catch (error) {
      // No-op on native if we can't reload
    }
  }, []);
  
  // Get query params for purchase success
  const params = useLocalSearchParams<{
    purchase_success?: string;
    purchase_error?: string;
    licenses?: string;
    product_id?: string;
    is_new?: string;
    phone_verified?: string;
  }>();
  
  // State for purchase success modal
  const [purchaseSuccessModal, setPurchaseSuccessModal] = useState(false);
  const [purchaseData, setPurchaseData] = useState<{
    licenses: number;
    productId: string;
    isNew: boolean;
  } | null>(null);

  // TODO(re-enable): phone_verified refresh after migration 0014 stabilizes in prod

  // Handle purchase success/error from checkout redirect
  useEffect(() => {
    const clearParams = () => {
      if (Platform.OS === 'web' && typeof window !== 'undefined' && window.history) {
        window.history.replaceState({}, document.title, window.location.pathname);
      }
    };

      // Purchase params handling - licenses will be refreshed via context in OwnerDashboardWrapper
      handlePurchaseParams({
      params,
      isOwnerProfile,
      t,
      setPurchaseData,
      setPurchaseSuccessModal,
      setAlertMessage,
      setAlertModalVisible,
      fetchCompanyLicenses: async () => {
        // This will be called but won't update context - OwnerDashboardWrapper handles refresh
        const response = await getCompanyLicenses();
        return response;
      },
      updateLicenseInfo: (info) => {
        // License info is now managed by context - OwnerDashboardWrapper will refresh
        // This is a no-op here, but kept for compatibility
      },
      clearPurchaseParams: clearParams,
    });
  }, [
    params.purchase_success,
    params.purchase_error,
    params.licenses,
    params.product_id,
    params.is_new,
    isOwnerProfile,
    t,
  ]);

  // Update sidebar state when screen size changes
  // Removed redundant currentWidth tracking to prevent cascading updates
  // useWindowDimensions() already provides reactive width updates
  useEffect(() => {
    if (isLargeScreen) {
      // Large screen: show desktop sidebar, hide mobile sidebar
      setDesktopSidebarVisible(true);
      setSidebarOpen(false);
    } else {
      // Small/tablet screen: hide desktop sidebar, mobile sidebar controlled by user
      setDesktopSidebarVisible(false);
      // Don't automatically open mobile sidebar, let user control it
    }
  }, [isLargeScreen]);

  const companyForm = useForm({ resolver: zodResolver(companySchema), defaultValues: FORM_DEFAULTS.COMPANY });
  const vehicleForm = useForm({ resolver: zodResolver(vehicleSchema), defaultValues: FORM_DEFAULTS.VEHICLE });
  const startOdometerForm = useForm({ resolver: zodResolver(odometerSchema), defaultValues: FORM_DEFAULTS.ODOMETER });
  const endOdometerForm = useForm({ resolver: zodResolver(odometerSchema), defaultValues: FORM_DEFAULTS.ODOMETER });

  // Only fetch driver data when user is a driver profile
  const { isSessionActive, isDriving, drivingTimeToday, drivingLimit, drivingWeekLimits, stateSince, actionInProgress, isStartingDriving, isStoppingDriving, fetchDrivingTime, handleStartSession, handleStartSessionWithProvidedLocation, handleStartDriving, handleStopDriving, fetchDriverState, fetchDriverStatus } = useTimeLogs({ 
    selectedVehicle,
    form: startOdometerForm,
    enabled: isDriverProfile, // Only fetch driver data for driver profile
    onWorkCompanyRequired: () => {
      setStartSessionModalVisible(false);
      router.push('/dashboard/work-company');
    },
  });

  const { isEndingSession, handleEndSessionConfirm, handleEndSessionConfirmWithProvidedLocation } = useEndSession({
    selectedVehicle,
    form: endOdometerForm,
    onSessionEnded: async () => {
      setEndSessionModalVisible(false);
      if (isDriverProfile) {
        await fetchDriverStatus();
      }
    }
  });

  const dismissAlertFlow = useCallback(() => {
    setAlertModalVisible(false);
    setAlertAction(null);
    setAlertMessage('');
  }, []);

  // If driving state changes (e.g. action handled via push), remove stale in-app alert modal.
  useEffect(() => {
    if (alertModalVisible) {
      dismissAlertFlow();
    }
  }, [dismissAlertFlow, isDriving]);

  // Pause foreground location updates while session modals are open to avoid UI churn/flicker
  const shouldTrackForegroundLocation =
    isDriverProfile &&
    !startSessionModalVisible &&
    !endSessionModalVisible &&
    !velocityWarningVisible &&
    !correctionModalVisible;

  const { location, error: foregroundLocationError } = useForegroundLocation(shouldTrackForegroundLocation);

  // Define custom handlers for session actions that users can directly trigger
  const onStartSession = async () => {
    // Refresh vehicles to ensure we have the latest count (in case they were just added)
    await fetchVehicleData(true);
    // Open session modal directly - vehicle selection is now integrated in the modal
    setStartSessionModalVisible(true);
  };

  const onEndSession = async () => {
    // Refresh vehicles to ensure we have the latest state before ending
    await fetchVehicleData(true);
    // If only one vehicle exists and none is selected, auto-select it
    if (!selectedVehicle && vehicles && vehicles.length === 1) {
      setSelectedVehicle(vehicles[0].id);
    }
    setEndSessionModalVisible(true);
  };

  const startDrivingInFlightRef = useRef(false);

  const onStartDriving = useCallback(async () => {
    if (startDrivingInFlightRef.current || stopDrivingInFlightRef.current || isDrivingRef.current) {
      return;
    }

    startDrivingInFlightRef.current = true;
    try {
      // Hook handles optimistic updates and loading state
      const result = await handleStartDriving();
      if (result === true) {
        setLocationPermissionError(false);
        setLocationSettingsError(false);
      } else if (result === 'location_permission_denied') {
        setLocationSettingsError(false);
        setLocationPermissionError(true);
      } else if (result === 'location_settings_invalid') {
        setLocationPermissionError(false);
        setLocationSettingsError(true);
      }
    } finally {
      startDrivingInFlightRef.current = false;
    }
  }, [handleStartDriving]);

  const isDrivingRef = useRef(isDriving);
  useEffect(() => {
    isDrivingRef.current = isDriving;
  }, [isDriving]);

  const stopDrivingInFlightRef = useRef(false);

  const onStopDriving = useCallback(async () => {
    if (stopDrivingInFlightRef.current || startDrivingInFlightRef.current || !isDrivingRef.current) {
      return;
    }

    stopDrivingInFlightRef.current = true;
    try {
      // Hook handles optimistic updates and loading state
      const result = await handleStopDriving();
      if (result === 'location_permission_denied') {
        setLocationSettingsError(false);
        setLocationPermissionError(true);
      } else if (result === 'location_settings_invalid') {
        setLocationPermissionError(false);
        setLocationSettingsError(true);
      }
    } finally {
      stopDrivingInFlightRef.current = false;
    }
  }, [handleStopDriving]);
  
  const onStartDrivingFromAlertFlow = useCallback(async () => {
    dismissAlertFlow();
    await onStartDriving();
  }, [dismissAlertFlow, onStartDriving]);

  const onStopDrivingFromAlertFlow = useCallback(async () => {
    dismissAlertFlow();
    await onStopDriving();
  }, [dismissAlertFlow, onStopDriving]);

  // Define alert message actions that will be called from the hook
  const showMovementAlert = useCallback(() => {
    setAlertMessage(t('alerts.movementDetected', 'You are moving but not in driving mode. Please start driving mode.'));
    setAlertAction(() => onStartDrivingFromAlertFlow);
    setAlertModalVisible(true);
  }, [onStartDrivingFromAlertFlow, t]);
  
  const showStationaryAlert = useCallback(() => {
    setAlertMessage(t('alerts.stationaryDetected', 'You have been stationary for a while. Consider stopping driving mode.'));
    setAlertAction(() => onStopDrivingFromAlertFlow);
    setAlertModalVisible(true);
  }, [onStopDrivingFromAlertFlow, t]);

  // Initialize location tracking with the alert handlers
  const {
    startBackgroundTracking,
    stopBackgroundTracking,
    trackingDegraded: backgroundTrackingDegraded,
  } = useLocationTracking({
    handleStopDriving: onStopDriving,
    isSessionActive,
    isDriving,
    onMovementAlert: showMovementAlert,
    onStationaryAlert: showStationaryAlert,
  });
  const trackingDegraded = Boolean(foregroundLocationError || backgroundTrackingDegraded);

  // Clear stale cache and force fresh data on initial mount
  // Consolidated with fetchInitialData to avoid duplicate /me/ calls
  const hasFetchedUserDataRef = useRef(false);
  
  useEffect(() => {
    const initializeCache = async () => {
      if (hasInitializedCache) return;
      
      // Set a timeout to prevent infinite loading
      const timeoutId = setTimeout(() => {
        if (!hasInitializedCache) {
          console.error('[DashboardHome] ⚠️ Initialization timeout! Forcing completion...');
          setInitializationTimeout(true);
          setHasInitializedCache(true);
        }
      }, 10000); // 10 second timeout
      
      try {
        // Check if this is the first run on this device
        const deviceInitialized = await AsyncStorage.getItem('deviceInitialized');
        // If first run on this device, clear everything and force onboarding
        if (!deviceInitialized) {
          await AsyncStorage.removeItem('onboardingData');
        }
      } catch (error) {
        console.error('[DashboardHome] ✗ Error checking device initialization:', error);
      }
      
      // User data is already fetched by useUser hook on mount, no need to fetch again here
      // The useUser hook's useEffect handles the initial /me/ call
      
      // Force fresh fetch of vehicle data if driver with active employment
      if (isDriverProfile && canOperateAsDriver) {
        try {
          await fetchVehicleData(true); // Force fetch, bypass cooldown
        } catch (error) {
          console.error('[DashboardHome] ✗ Error fetching vehicle data:', error);
        }
      }
      
      clearTimeout(timeoutId);
      setHasInitializedCache(true);
    };
    
    initializeCache();
  }, [isDriverProfile, canOperateAsDriver]);

  // Redirect drivers to onboarding based on various conditions
  // Uses centralized onboarding redirect logic
  useEffect(() => {
    const checkOnboardingAndRedirect = async () => {
      // Only check redirect after cache is initialized
      if (!hasInitializedCache) {
        return;
      }
      
      // EXPLICIT: Skip onboarding check if user is owner or has owned companies
      const isOwner = userData?.groups?.includes('Owner');
      const hasOwnedCompanies = userData?.owned_companies && userData.owned_companies.length > 0;
      
      // Don't redirect owners - they have their own onboarding flow
      if (isOwner || hasOwnedCompanies || !isDriverProfile) {
        return;
      }
      
      if (isRedirectingToOnboarding) {
        return;
      }

      // Self-pay entitlement is tri-state. While the paywall request is still
      // hydrating, the dashboard must not ask the redirect helper to classify
      // the driver: a transient null must never become a checkout/onboarding
      // bounce on reload. A later conclusive server state decides the route.
      if (hasSelfPayLicenseTriState === null) {
        return;
      }

      // The authoritative state owns every redirect, including identity,
      // employment, pending, and self-pay routes. Do not collapse non-generic
      // routes back into /onboarding based on local dashboard signals.
      try {
        const redirectResult = await getOnboardingRedirect({
          userData,
          companies: companies,
          employments: employments,
          hasVehicles: hasVehicles ?? undefined,
          checkLicenses: false, // Skip license check for drivers
          hasSelfPayLicense: hasSelfPayLicenseTriState, // presentation only
        });

        if (redirectResult.shouldRedirect && redirectResult.route !== '/dashboard/home') {
          setIsRedirectingToOnboarding(true);
          setTimeout(() => safeRedirect(redirectResult.route), 100);
        }
      } catch (error) {
        console.error('[DashboardHome] Error checking redirect conditions:', error);
      }
    };
    
    checkOnboardingAndRedirect();
  }, [isDriverProfile, hasActiveEmployment, hasSelfPayLicenseTriState, hasVehicles, isRedirectingToOnboarding, hasInitializedCache, userData, companies, employments, safeRedirect]);

  // Configuração inicial e atualização da notificação (only for drivers)
  useEffect(() => {
    if (!isDriverProfile) return;

    Sentry.addBreadcrumb({
      category: 'dashboard.home.notifications.init',
      level: 'info',
      data: {
        platform: Platform.OS,
        isDriverProfile,
        isSessionActive,
        isDriving,
      },
    });
    
    const initializeNotifications = async () => {
      setupNotificationHandler();
      await setupNotificationChannel();
      // Only show notification if there is an active session
      if (isSessionActive) {
        await updateDrivingNotification(isDriving);
      }
    };
    initializeNotifications();
  }, [isDriverProfile, isSessionActive, isDriving]);

  // Atualiza a notificação quando o estado de condução muda (only for drivers)
  useEffect(() => {
    if (!isDriverProfile) return;
    
    if (isSessionActive) {
      updateDrivingNotification(isDriving);
    } else if (Platform.OS !== 'web') {
      Notifications.dismissNotificationAsync(DRIVING_STATUS_NOTIFICATION_ID);
    }
  }, [isDriverProfile, isDriving, isSessionActive]);

  // Foreground/app-active owner for alert reconciliation. Keeps alert cleanup away from TaskManager
  // and never touches the persistent driving-status notification.
  useEffect(() => {
    return subscribeBackgroundAlertReconciliation({
      isDriverProfile,
      isSessionActive,
      isDriving,
    });
  }, [isDriverProfile, isSessionActive, isDriving]);

  // Listener para ações da notificação (only on native, only for drivers)
  useEffect(() => {
    if (Platform.OS === 'web' || !isDriverProfile) return;

    Sentry.addBreadcrumb({
      category: 'dashboard.home.notifications.listener',
      level: 'info',
      data: {
        platform: Platform.OS,
        isDriverProfile,
      },
    });

    const subscription = Notifications.addNotificationResponseReceivedListener(async (response) => {
      if (!isDrivingStatusNotificationResponse(response)) {
        return;
      }

      const actionId = response.actionIdentifier;
      if (actionId === START_DRIVING_ACTION_ID && !isDriving) {
        track('driver_notification_driving_status_tapped', {
          action: 'start_driving',
          source: 'persistent_notification',
        });
        await onStartDriving();
      } else if (actionId === STOP_DRIVING_ACTION_ID && isDriving) {
        track('driver_notification_driving_status_tapped', {
          action: 'stop_driving',
          source: 'persistent_notification',
        });
        await onStopDriving();
      }
    });
    return () => subscription.remove();
  }, [isDriverProfile, isDriving, onStartDriving, onStopDriving]);

  // Gerenciamento do rastreamento em background (somente para perfil de motorista)
  useEffect(() => {
    Sentry.addBreadcrumb({
      category: 'dashboard.home.location.background_tracking',
      level: 'info',
      data: {
        platform: Platform.OS,
        isDriverProfile,
        isSessionActive,
      },
    });

    if (!isDriverProfile) {
      stopBackgroundTracking();
      return;
    }

    if (isSessionActive) {
      startBackgroundTracking();
    } else {
      stopBackgroundTracking();
    }
  }, [isSessionActive, isDriverProfile]);

  // Verificação de permissões (somente para perfil de motorista; skip on web where applicable)
  useEffect(() => {
    const checkPermissions = async () => {
      if (!isDriverProfile) return; // Owners shouldn't be prompted for location/notification permissions
      if (Platform.OS === 'web') return; // Skip permission checks on web

      const { status: notificationStatus } = await Notifications.getPermissionsAsync();
      const { status: foregroundStatus } = await Location.getForegroundPermissionsAsync();
      const { status: backgroundStatus } = await Location.getBackgroundPermissionsAsync();

      // On iOS, background location permission is not available in simulator and
      // is only prompted after the user has granted foreground and the app demonstrates need.
      // Treat 'undetermined' as acceptable so the app doesn't block on first launch.
      const isBackgroundOk = backgroundStatus === 'granted' || backgroundStatus === 'undetermined';

      if (notificationStatus !== 'granted' || foregroundStatus !== 'granted' || !isBackgroundOk) {
        if (foregroundStatus !== 'granted' || !isBackgroundOk) {
          setLocationPermissionError(true);
        } else {
          router.push('/dashboard/settings');
        }
      } else {
        setLocationPermissionError(false);
      }
    };
    checkPermissions();
  }, [isDriverProfile]);

  // Update this useEffect to set form values
  useEffect(() => {
    const fetchLatestOdometer = async () => {
      if (selectedVehicle) {
        try {
          const response = await apiFetch<{ odometer: number | null; timestamp: string | null }>(
            `${API_ENDPOINTS.TIMELOGS}latest-odometer/?vehicle_id=${selectedVehicle}`
          );
          if (response.data.odometer !== null) {
            setLatestOdometer(response.data.odometer);
          } else {
            setLatestOdometer(null);
          }
        } catch (error) {
          console.error('Error fetching latest odometer:', error);
          setLatestOdometer(null);
        }
      }
    };

    fetchLatestOdometer();
  }, [selectedVehicle]); // Only depend on selectedVehicle

  // Add new useEffect to fetch latest odometer when modals open
  useEffect(() => {
    const fetchLatestOdometer = async () => {
      if (selectedVehicle && (startSessionModalVisible || endSessionModalVisible)) {
        try {
          const response = await apiFetch<{ odometer: number | null; timestamp: string | null }>(
            `${API_ENDPOINTS.TIMELOGS}latest-odometer/?vehicle_id=${selectedVehicle}`
          );
          if (response.data.odometer !== null) {
            if (startSessionModalVisible) {
              // Always sync odometer with currently selected vehicle while starting session.
              // This guarantees vehicle switch -> odometer switch in the same modal flow.
              startOdometerForm.setValue('odometer', response.data.odometer, {
                shouldDirty: false,
                shouldTouch: false,
                shouldValidate: false,
              });
            }
            if (endSessionModalVisible) {
              const endFieldState = endOdometerForm.getFieldState('odometer');
              const endOdometerTouched = endFieldState.isTouched;
              const endOdometerDirty = endFieldState.isDirty;
              const endOdometerCurrentValue = endOdometerForm.getValues('odometer');
              const endOdometerIsEmptyOrZero =
                endOdometerCurrentValue === undefined ||
                endOdometerCurrentValue === null ||
                endOdometerCurrentValue === '' ||
                Number(endOdometerCurrentValue) === 0;
              if (!endOdometerTouched && !endOdometerDirty && endOdometerIsEmptyOrZero) {
                endOdometerForm.setValue('odometer', response.data.odometer);
              }
            }
            setLatestOdometer(response.data.odometer);
          }
        } catch (error) {
          console.error('Error fetching latest odometer:', error);
        }
      }
    };

    fetchLatestOdometer();
  }, [selectedVehicle, startSessionModalVisible, endSessionModalVisible]);

  const handleStartSessionSubmit = async (data: OdometerFormData) => {
    setIsStartingSession(true);
    try {
      const result = await handleStartSession(data);
      if (result === true) {
        setStartSessionModalVisible(false);
        // No need for extra API calls - backend now returns complete state
      } else if (result === 'location_denied') {
        startOdometerForm.setError('odometer', {
          type: 'manual',
          message: 'LOCATION_PERMISSION_DENIED',
        });
      } else if (isSessionResultObject(result) && result.status === 'velocity_warning') {
        // Show velocity warning modal
        setPendingSessionData(data);
        setPendingLocationWkt(null);
        setVelocityWarningData(result.warning);
        setVelocityWarningContext('start');
        setVelocityWarningVisible(true);
        setStartSessionModalVisible(false);
      } else if (isSessionResultObject(result) && result.status === 'odometer_decrease') {
        setPendingSessionData(data);
        setPreviousSessionOut(result.previousSessionOut ?? null);
        setNewOdometerValue(result.newOdometer ?? null);
        setCorrectionContext('start');
        setCorrectionModalVisible(true);
        setStartSessionModalVisible(false);
      }
      return result;
    } finally {
      setIsStartingSession(false);
    }
  };

  // Handle velocity warning confirmation - proceed with session start
  const handleVelocityWarningConfirm = async () => {
    if (!pendingSessionData || !selectedVehicle) return;

    if (velocityWarningContext === 'end') {
      setIsVelocityConfirming(true);
      setVelocityWarningVisible(false);
      try {
        track('driver_session_end_attempt', {
          vehicle_id: selectedVehicle,
          confirm_velocity: true,
          source: 'velocity_warning',
        });
        const location = pendingLocationWkt || await getLocationString();
        const response = await endSession(selectedVehicle, pendingSessionData.odometer, location, pendingSessionData.observations, true);
        if (response.ok) {
          track('driver_session_ended', {
            vehicle_id: selectedVehicle,
            confirm_velocity: true,
            source: 'velocity_warning',
          });
          if (isDriverProfile) {
            await fetchDriverStatus();
          }
          setPendingSessionData(null);
          setPendingLocationWkt(null);
          setVelocityWarningData(null);
          setVelocityWarningContext(null);
        } else {
          endOdometerForm.setError('odometer', {
            type: 'manual',
            message: t('errorEndingSession'),
          });
          setEndSessionModalVisible(true);
        }
      } catch (error: any) {
        console.error('Error ending session after velocity confirmation:', error);
        if (error && error.message === 'LOCATION_PERMISSION_DENIED') {
          endOdometerForm.setError('odometer', {
            type: 'manual',
            message: 'LOCATION_PERMISSION_DENIED',
          });
        } else {
          endOdometerForm.setError('odometer', {
            type: 'manual',
            message: t('errorEndingSession'),
          });
        }
        setEndSessionModalVisible(true);
      } finally {
        setIsVelocityConfirming(false);
      }
      return;
    }

    setIsStartingSession(true);
    setVelocityWarningVisible(false);
    
    try {
      track('driver_session_start_attempt', {
        vehicle_id: selectedVehicle,
        confirm_velocity: true,
        source: 'velocity_warning',
      });
      const location = pendingLocationWkt || await getLocationString();
      const response = await startSession(selectedVehicle, pendingSessionData.odometer, location, true);
      
      if (response.ok) {
        track('driver_session_started', {
          vehicle_id: selectedVehicle,
          confirm_velocity: true,
          source: 'velocity_warning',
        });
        // Refresh driver state from hook
        await fetchDriverState();
        await fetchDrivingTime();
        setPendingSessionData(null);
        setPendingLocationWkt(null);
        setVelocityWarningData(null);
        setVelocityWarningContext(null);
      } else {
        // Handle error - show error in form
        startOdometerForm.setError('odometer', {
          type: 'manual',
          message: t('errorStartingSession'),
        });
        setStartSessionModalVisible(true);
      }
    } catch (error: any) {
      console.error('Error starting session after velocity confirmation:', error);
      if (error && error.message === 'LOCATION_PERMISSION_DENIED') {
        startOdometerForm.setError('odometer', {
          type: 'manual',
          message: 'LOCATION_PERMISSION_DENIED',
        });
      } else {
        startOdometerForm.setError('odometer', {
          type: 'manual',
          message: t('errorStartingSession'),
        });
      }
      setStartSessionModalVisible(true);
    } finally {
      setIsStartingSession(false);
    }
  };

  const handleOdometerCorrection = async (
    correctedOdometer: number,
    observations: string,
    providedLocationWkt?: string,
  ) => {
    if (!previousSessionOut || !pendingSessionData || !selectedVehicle) return;
    
    setIsCorrectingOdometer(true);
    
    try {
      const location = providedLocationWkt || await getLocationString();
      const correctionResponse = await correctOdometer(
        previousSessionOut.id,
        correctedOdometer,
        observations
      );
      
      if (!correctionResponse.ok) {
        throw new Error('Failed to correct odometer');
      }

      if (correctionContext === 'end') {
        const sessionResponse = await endSession(selectedVehicle, pendingSessionData.odometer, location, pendingSessionData.observations);
        if (sessionResponse.ok) {
          if (isDriverProfile) {
            await fetchDriverStatus();
          }
          setCorrectionModalVisible(false);
          setCorrectionContext(null);
          setPreviousSessionOut(null);
          setNewOdometerValue(null);
          setPendingSessionData(null);
        } else {
          throw new Error('Failed to end session after correction');
        }
      } else {
        const sessionResponse = await startSession(selectedVehicle, pendingSessionData.odometer, location);
        if (sessionResponse.ok) {
          await fetchDriverState();
          await fetchDrivingTime();
          setCorrectionModalVisible(false);
          setCorrectionContext(null);
          setPreviousSessionOut(null);
          setNewOdometerValue(null);
          setPendingSessionData(null);
        } else {
          throw new Error('Failed to start session after correction');
        }
      }
    } catch (error) {
      console.error('Error correcting odometer:', error);
      const isEnd = correctionContext === 'end';
      const formRef = isEnd ? endOdometerForm : startOdometerForm;
      formRef.setError('odometer', {
        type: 'manual',
        message: t(isEnd ? 'errorEndingSession' : 'errorStartingSession'),
      });
      setCorrectionModalVisible(false);
      setCorrectionContext(null);
      if (isEnd) {
        setEndSessionModalVisible(true);
      } else {
        setStartSessionModalVisible(true);
      }
    } finally {
      setIsCorrectingOdometer(false);
    }
  };

  const handleStartSessionWithProvidedLocationWrapper = async (data: OdometerFormData, locationWkt: string): Promise<boolean> => {
    setIsStartingSession(true);
    try {
      const result = await handleStartSessionWithProvidedLocation(data, locationWkt);
      if (result === true) {
        setStartSessionModalVisible(false);
        return true;
      } else if (isSessionResultObject(result) && result.status === 'velocity_warning') {
        setPendingSessionData(data);
        setPendingLocationWkt(locationWkt);
        setVelocityWarningData(result.warning);
        setVelocityWarningContext('start');
        setVelocityWarningVisible(true);
        setStartSessionModalVisible(false);
        return false;
      } else if (isSessionResultObject(result) && result.status === 'odometer_decrease') {
        setPendingSessionData(data);
        setPreviousSessionOut(result.previousSessionOut ?? null);
        setNewOdometerValue(result.newOdometer ?? null);
        setCorrectionContext('start');
        setCorrectionModalVisible(true);
        setStartSessionModalVisible(false);
        return false;
      }
      return false;
    } finally {
      setIsStartingSession(false);
    }
  };

  const handleEndSessionSubmit = async (data: OdometerFormData) => {
    try {
      const result = await handleEndSessionConfirm(data);
      if (result === true) {
        setEndSessionModalVisible(false);
      } else if (isSessionResultObject(result) && result.status === 'velocity_warning') {
        setPendingSessionData(data);
        setPendingLocationWkt(result.location ?? null);
        setVelocityWarningData(result.warning);
        setVelocityWarningContext('end');
        setVelocityWarningVisible(true);
        setEndSessionModalVisible(false);
      } else if (isSessionResultObject(result) && result.status === 'odometer_decrease') {
        setPendingSessionData(data);
        setPreviousSessionOut(result.previousSessionOut ?? null);
        setNewOdometerValue(result.newOdometer ?? null);
        setCorrectionContext('end');
        setCorrectionModalVisible(true);
        setEndSessionModalVisible(false);
      } else if (result === 'location_denied') {
        endOdometerForm.setError('odometer', {
          type: 'manual',
          message: 'LOCATION_PERMISSION_DENIED',
        });
      }
      return result;
    } catch (error) {
      console.error('Error ending session:', error);
      return false;
    }
  };

  const handleEndSessionSubmitWithProvidedLocation = async (data: OdometerFormData, locationWkt: string): Promise<boolean> => {
    try {
      const result = await handleEndSessionConfirmWithProvidedLocation(data, locationWkt);
      if (result === true) {
        setEndSessionModalVisible(false);
      } else if (isSessionResultObject(result) && result.status === 'velocity_warning') {
        setPendingSessionData(data);
        setPendingLocationWkt(result.location ?? locationWkt);
        setVelocityWarningData(result.warning);
        setVelocityWarningContext('end');
        setVelocityWarningVisible(true);
        setEndSessionModalVisible(false);
      } else if (isSessionResultObject(result) && result.status === 'odometer_decrease') {
        setPendingSessionData(data);
        setPreviousSessionOut(result.previousSessionOut ?? null);
        setNewOdometerValue(result.newOdometer ?? null);
        setCorrectionContext('end');
        setCorrectionModalVisible(true);
        setEndSessionModalVisible(false);
      }
      return result === true;
    } catch (error) {
      console.error('Error ending session with provided location:', error);
      return false;
    }
  };

  // Retry requesting location permission
  const retryLocationPermission = async () => {
    setLocationPermissionError(false);

    try {
      const { status: foregroundStatus } = await Location.requestForegroundPermissionsAsync();
      let backgroundStatus: Location.PermissionStatus | null = null;

      if (Platform.OS !== 'web') {
        const backgroundPermission = await Location.requestBackgroundPermissionsAsync();
        backgroundStatus = backgroundPermission.status;
      }

      const isForegroundGranted = foregroundStatus === 'granted';
      const isBackgroundGranted = Platform.OS === 'web' || backgroundStatus === 'granted';

      if (!isForegroundGranted || !isBackgroundGranted) {
        setLocationPermissionError(true);
      } else {
        setLocationPermissionError(false);
      }
    } catch (error) {
      console.error('Error retrying location permissions:', error);
      setLocationPermissionError(true);
    }
  };


  // Polling para atualizar dados
  useEffect(() => {
    let pollInterval: ReturnType<typeof setInterval>;
    let lastPollTime = Date.now();
    const POLL_COOLDOWN = 30000; // 30 seconds cooldown between polls

    const startPolling = async () => {
      // Skip polling if action is in progress
      if (actionInProgress) return;
      
      const now = Date.now();
      if (now - lastPollTime < POLL_COOLDOWN) return;
      
      try {
        // Only fetch vehicle data if the driver may operate and has no vehicles yet
        if (canOperateAsDriver && hasVehicles === null && !isLoading) {
          await fetchVehicleData();
        }
        
        lastPollTime = now;
      } catch (error) {
        console.error('Error in polling:', error);
      }
    };

    // Only start polling if the driver may operate
    if (canOperateAsDriver) {
      startPolling(); // Initial fetch
      pollInterval = setInterval(startPolling, POLL_COOLDOWN);
    }

    return () => {
      if (pollInterval) {
        clearInterval(pollInterval);
      }
    };
  }, [canOperateAsDriver, hasVehicles, isLoading, actionInProgress]);

  // Check if owner has EVER had licenses across ALL companies
  // Returns true if ANY company has ever_had_licenses === true
  // Uses centralized onboarding redirect utility
  const checkOwnerHasEverHadLicenses = async (): Promise<boolean> => {
    if (!companies || companies.length === 0) {
      return false;
    }

    try {
      // Use centralized utility function
      const shouldRedirect = await shouldRedirectToOwnerOnboarding(userData, companies);
      // Return inverse: if should redirect, then has no licenses
      return !shouldRedirect;
    } catch (error) {
      console.error('Error checking owner licenses across companies:', error);
      return false;
    }
  };

  // Fetch company stats when company changes (LicenseContext handles license fetching)
  useEffect(() => {
    const fetchData = async () => {
      if (userData?.groups?.includes('Owner')) {
        await fetchCompanyStats();
      }
    };

    fetchData().catch(error => {
      console.error('Error in dashboard data fetch:', error);
    });
  }, [selectedCompanyId, userData?.groups]);

  // SIMPLIFIED: Check if owner has EVER had licenses in ANY company
  // This is ONLY for login redirect - check once when companies first load
  // Don't re-check when companies change - that's not needed for login redirect
  useEffect(() => {
    const checkLicensesIfNeeded = async () => {
      // Only check if user is owner
      if (!userData?.groups?.includes('Owner')) {
        return;
      }
      // The extracted owner path never consults local license history. Its
      // screen follows GET /api/onboarding/state directly.
      if (isOwnerHomeScreenEnabled()) {
        return;
      }

      // IMPORTANT: If licenses were already verified (persistent flag), skip this check
      // This prevents duplicate checks and race conditions across navigation
      const hasLicenses = await AsyncStorage.getItem('ownerHasLicenses');
      if (hasLicenses === 'true') {
        // Set to true to prevent any redirects
        setOwnerEverHadLicenses(true);
        hasCheckedLicensesRef.current = true;
        return;
      }

      // Wait for companies to load
      if (isCompaniesLoading) {
        return;
      }

      // If we're already checking, don't start another check
      if (isCheckingAllCompaniesForLicenses) {
        return;
      }

      // If we already checked, don't check again
      // This is only for login redirect, so we only need to check once
      if (hasCheckedLicensesRef.current) {
        return;
      }

      // Wait until CompanyContext has completed its first load.
      // On the initial dashboard render, the companies array can still be empty
      // for a brief moment before refreshCompanies finishes. Finalizing a "no licenses"
      // result too early leaves the owner stuck on the loading screen.
      if (!hasCompaniesLoadedOnce) {
        return;
      }

      // If no companies after the first load, owner has no licenses
      if (!companies || companies.length === 0) {
        setOwnerEverHadLicenses(false);
        setIsCheckingAllCompaniesForLicenses(false);
        hasCheckedLicensesRef.current = true;
        return;
      }

      // Check all companies - only once on first load
      setIsCheckingAllCompaniesForLicenses(true);
      hasCheckedLicensesRef.current = true; // Mark as checked
      let active = true;
      
      checkOwnerHasEverHadLicenses().then((result) => {
        if (active) {
          setOwnerEverHadLicenses(result);
          setIsCheckingAllCompaniesForLicenses(false);
        }
      }).catch((error) => {
        console.error('Error checking owner licenses:', error);
        if (active) {
          setOwnerEverHadLicenses(false);
          setIsCheckingAllCompaniesForLicenses(false);
        }
      });

      return () => {
        active = false;
      };
    };

    checkLicensesIfNeeded();
  }, [companies, hasCompaniesLoadedOnce, isCompaniesLoading, userData?.groups]); // Check when companies first load

  // Owner redirect is handled in OwnerDashboardWrapper (single source, avoids duplicate effects)

  // Fetch company statistics - use ref to prevent duplicate calls
  const hasFetchedStatsRef = useRef(false);
  const isFetchingStatsRef = useRef(false);

  // Fetch company statistics
  const fetchCompanyStats = useCallback(async () => {
    if (!userData?.groups?.includes('Owner')) {
      return;
    }
    
    // Prevent duplicate calls
    if (isFetchingStatsRef.current) {
      return;
    }
    
    // Only fetch once per company
    if (hasFetchedStatsRef.current && companyStats !== null) {
      return;
    }

    isFetchingStatsRef.current = true;
    try {
      const response = await getCompanyStatistics();
      if (response.ok) {
        setCompanyStats(response.data);
        hasFetchedStatsRef.current = true;
      }
    } catch (error) {
      console.error('Error fetching company statistics:', error);
    } finally {
      isFetchingStatsRef.current = false;
    }
  }, [userData?.groups, companyStats]);

  // Fetch owner statistics when user data is available (only once)
  useEffect(() => {
    if (userData?.groups?.includes('Owner') && userData?.company && !hasFetchedStatsRef.current) {
      fetchCompanyStats();
    }
  }, [userData?.company?.nipc, userData?.groups, fetchCompanyStats]);

  // Removed driver locations fetch from dashboard

  // Add this function to handle successful license purchase
  // Note: This will be wrapped in OwnerDashboardWrapper to access useLicense hook
  const handleLicensePurchaseSuccessBase = async (refreshLicenses: () => Promise<void>) => {
    try {
      // Show initial success message
      setAlertMessage(t('buyLicense.purchaseSuccessUpdating', 'License purchase successful! Updating your account...'));
      setAlertAction(null);
      setAlertModalVisible(true);

      // Function to attempt refresh with delay
      const attemptRefresh = async (attempt: number, maxAttempts: number = 3) => {
        if (attempt > maxAttempts) {
          setAlertMessage(t('buyLicense.purchaseSuccessRefreshError', 'License purchase successful, but there was an error refreshing your account. Please refresh the page.'));
          setAlertAction(() => () => { void reloadApp(); });
          setAlertModalVisible(true);
          return;
        }

        // Wait for webhook to process (2 seconds * attempt number)
        await new Promise(resolve => setTimeout(resolve, 2000 * attempt));

        try {
          // Refresh both license info and user data
          await Promise.all([
            refreshLicenses(),
            fetchUserData()
          ]);

          setAlertMessage(t('buyLicense.purchaseSuccessUpdated', 'License purchase successful! Your account has been updated.'));
          setAlertAction(null);
          setAlertModalVisible(true);
          return;
        } catch (error) {
          console.error(`Error refreshing data (attempt ${attempt}):`, error);
          await attemptRefresh(attempt + 1);
        }
      };

      // Start the refresh attempts
      await attemptRefresh(1);
    } catch (error) {
      console.error('Error in purchase success handler:', error);
      setAlertMessage(t('buyLicense.purchaseSuccessRefreshError', 'License purchase successful, but there was an error refreshing your account. Please refresh the page.'));
      setAlertAction(() => () => { void reloadApp(); });
      setAlertModalVisible(true);
    }
  };

  // Show loading until user employment status is known AND userData is loaded
  if (hasActiveEmployment === null || !isUserDataLoaded) {
    return (
      <LoadingScreen message={t('loading.userInformation', 'Loading user information...')} />
    );
  }

  if (partnerHome === 'checking') {
    return <LoadingScreen message={t('loading.userInformation', 'Loading user information...')} />;
  }
  if (partnerHome) {
    return <Redirect href={partnerHome} />;
  }

  // The alert dialog used to live only inside the driver branch, so an owner
  // action that sets alertMessage (e.g. "invitation sent") showed nothing at
  // all. One definition, rendered by every branch.
  const alertPortal = (
    <Portal>
      <Dialog visible={alertModalVisible} onDismiss={dismissAlertFlow} style={Platform.OS === 'web' ? {
        maxWidth: 500,
        width: '90%',
        alignSelf: 'center',
      } : {}}>
        <Dialog.Title>{t('alerts.alert', 'Alert')}</Dialog.Title>
        <Dialog.Content>
          <Text testID="dashboard-alert-message" variant="bodyMedium">{alertMessage}</Text>
        </Dialog.Content>
        <Dialog.Actions>
          <Button onPress={dismissAlertFlow}>{t('cancel', 'Cancel')}</Button>
          <Button onPress={() => { void alertAction?.(); }}>{t('alerts.confirm', 'Confirm')}</Button>
        </Dialog.Actions>
      </Dialog>
    </Portal>
  );

  // Render based on active profile
  if (isOwnerProfile) {
    // Feature-flagged extraction: mirrors the driver pattern.
    // Legacy inline branch preserved verbatim as rollback path.
    if (isOwnerHomeScreenEnabled()) {
      return (
        <>
        {alertPortal}
        <LicenseProvider shouldFetch={shouldFetchLicenses}>
          <OwnerHomeScreen
            userData={userData}
            companyStats={companyStats}
            width={width}
            isLargeScreen={isLargeScreen}
            desktopSidebarVisible={desktopSidebarVisible}
            sidebarOpen={sidebarOpen}
            setSidebarOpen={setSidebarOpen}
            handleLogout={handleLogout}
            hasDriver={hasDriver}
            effectiveProfile={effectiveProfile}
            updateProfile={updateProfile}
            router={router}
            theme={theme}
            buyLicensesModalVisible={buyLicensesModalVisible}
            setBuyLicensesModalVisible={setBuyLicensesModalVisible}
            inviteModalVisible={inviteModalVisible}
            setInviteModalVisible={setInviteModalVisible}
            handleLicensePurchaseSuccessBase={handleLicensePurchaseSuccessBase}
            setAlertMessage={setAlertMessage}
            setAlertModalVisible={setAlertModalVisible}
            t={t}
            safeRedirect={safeRedirect}
            completeDriverProfileModalVisible={completeDriverProfileModalVisible}
            setCompleteDriverProfileModalVisible={setCompleteDriverProfileModalVisible}
            onCompleteDriverProfileSuccess={handleCompleteDriverProfileSuccess}
            onBecomeDriver={handleOwnerBecomeDriver}

          />
        </LicenseProvider>
        </>
      );
    }

    // If we're still checking licenses across all companies, show loading
    if (isCheckingAllCompaniesForLicenses) {
      return <LoadingScreen message={t('ownerDashboard.checkingLicenses', 'Verificando licenças...')} />;
    }

    // ALL-155: an owner without licence history renders the wrapper below, whose
    // server-authoritative redirect sends an unfinished onboarding to the owner
    // chat and keeps a registration-only owner on the dashboard.

    // If we know owner has licenses (in any company), render immediately
    // LicenseContext will handle fetching current company's license info
    if (ownerEverHadLicenses === null && !initializationTimeout) {
      // Still checking - but we already checked above, so this shouldn't happen
      // But if it does, don't block rendering if we have a timeout
      return <LoadingScreen message={t('ownerDashboard.loadingDashboard', 'Carregando dashboard...')} />;
    }

    // Wrap owner dashboard with LicenseProvider (handles current company's license)
    return (
      <>
      {alertPortal}
      <LicenseProvider shouldFetch={shouldFetchLicenses}>
        <OwnerDashboardWrapper
          userData={userData}
          companyStats={companyStats}
          desktopSidebarVisible={desktopSidebarVisible}
          sidebarOpen={sidebarOpen}
          setSidebarOpen={setSidebarOpen}
          width={width}
          isLargeScreen={isLargeScreen}
          handleLogout={handleLogout}
          hasDriver={hasDriver}
          effectiveProfile={effectiveProfile}
          updateProfile={updateProfile}
          router={router}
          theme={theme}
          buyLicensesModalVisible={buyLicensesModalVisible}
          setBuyLicensesModalVisible={setBuyLicensesModalVisible}
          inviteModalVisible={inviteModalVisible}
          setInviteModalVisible={setInviteModalVisible}
          handleLicensePurchaseSuccessBase={handleLicensePurchaseSuccessBase}
          setAlertMessage={setAlertMessage}
          setAlertModalVisible={setAlertModalVisible}
          t={t}
          safeRedirect={safeRedirect}
          completeDriverProfileModalVisible={completeDriverProfileModalVisible}
          setCompleteDriverProfileModalVisible={setCompleteDriverProfileModalVisible}
          onCompleteDriverProfileSuccess={handleCompleteDriverProfileSuccess}
          onBecomeDriver={handleOwnerBecomeDriver}

        />
      </LicenseProvider>
      </>
    );
  }

  // If selected profile is Driver, follow driver flow
  if (isDriverProfile) {
    // Feature-flagged extraction: when EXPO_PUBLIC_DRIVER_HOME_SCREEN_ENABLED
    // is enabled at bundle time, render the extracted DriverHomeScreen. The
    // legacy inline branch below is preserved verbatim as the rollback path —
    // flipping the flag off restores it without any other code change.
    if (isDriverHomeScreenEnabled()) {
      return (
        <DriverHomeScreen
          hasActiveEmployment={hasActiveEmployment}
          hasSelfPayLicense={hasSelfPayLicenseTriState}
          paymentIssue={paymentIssue}
          onFixPayment={paymentIssue ? fixSelfPayPayment : undefined}
          onSelfPay={goToSelfPay}
          hasVehicles={hasVehicles}
          isLoading={isLoading}
          employments={employments}
          hasDriver={hasDriver}
          hasOwner={hasOwner}
          effectiveProfile={effectiveProfile}
          updateProfile={updateProfile}
          hasInitializedCache={hasInitializedCache}
          initializationTimeout={initializationTimeout}
          isRedirectingToOnboarding={isRedirectingToOnboarding}
          reloadApp={reloadApp}
          width={width}
          isLargeScreen={isLargeScreen}
          desktopSidebarVisible={desktopSidebarVisible}
          sidebarOpen={sidebarOpen}
          setSidebarOpen={setSidebarOpen}
          theme={theme}
          handleLogout={handleLogout}
          companyForm={companyForm}
          handleAddCompany={handleAddCompany}
          modalVisible={employmentModalVisible}
          setModalVisible={setModalVisible}
          fromPendingWarning={fromPendingWarning}
          setFromPendingWarning={setFromPendingWarning}
          vehicleModalVisible={vehicleModalVisible}
          setVehicleModalVisible={setVehicleModalVisible}
          vehicleForm={vehicleForm}
          handleAddVehicle={handleAddVehicle}
          localRegNumber={localRegNumber}
          setLocalRegNumber={setLocalRegNumber}
          formatRegistrationNumber={formatRegistrationNumber}
          location={location}
          trackingDegraded={trackingDegraded}
          isSessionActive={isSessionActive}
          isDriving={isDriving}
          drivingTimeToday={drivingTimeToday}
          drivingLimit={drivingLimit}
          drivingWeekLimits={drivingWeekLimits}
          stateSince={stateSince}
          actionInProgress={actionInProgress}
          onStartSession={onStartSession}
          onEndSession={onEndSession}
          onStartDriving={onStartDriving}
          onStopDriving={onStopDriving}
          isStartingSession={isStartingSession}
          isEndingSession={isEndingSession}
          isStartingDriving={isStartingDriving}
          isStoppingDriving={isStoppingDriving}
          isVelocityConfirming={isVelocityConfirming}
          showMovementAlert={showMovementAlert}
          showStationaryAlert={showStationaryAlert}
          startSessionModalVisible={startSessionModalVisible}
          setStartSessionModalVisible={setStartSessionModalVisible}
          endSessionModalVisible={endSessionModalVisible}
          setEndSessionModalVisible={setEndSessionModalVisible}
          startOdometerForm={startOdometerForm}
          endOdometerForm={endOdometerForm}
          handleStartSessionSubmit={handleStartSessionSubmit}
          handleStartSessionWithProvidedLocationWrapper={handleStartSessionWithProvidedLocationWrapper}
          handleEndSessionSubmit={handleEndSessionSubmit}
          handleEndSessionSubmitWithProvidedLocation={handleEndSessionSubmitWithProvidedLocation}
          selectedVehicle={selectedVehicle}
          setSelectedVehicle={setSelectedVehicle}
          vehicles={vehicles}
          latestOdometer={latestOdometer}
          retryLocationPermission={retryLocationPermission}
          velocityWarningVisible={velocityWarningVisible}
          setVelocityWarningVisible={setVelocityWarningVisible}
          velocityWarningData={velocityWarningData}
          setVelocityWarningData={setVelocityWarningData}
          velocityWarningContext={velocityWarningContext}
          setVelocityWarningContext={setVelocityWarningContext}
          pendingSessionData={pendingSessionData}
          setPendingSessionData={setPendingSessionData}
          setPendingLocationWkt={setPendingLocationWkt}
          handleVelocityWarningConfirm={handleVelocityWarningConfirm}
          correctionModalVisible={correctionModalVisible}
          setCorrectionModalVisible={setCorrectionModalVisible}
          correctionContext={correctionContext}
          setCorrectionContext={setCorrectionContext}
          previousSessionOut={previousSessionOut}
          setPreviousSessionOut={setPreviousSessionOut}
          newOdometerValue={newOdometerValue}
          setNewOdometerValue={setNewOdometerValue}
          handleOdometerCorrection={handleOdometerCorrection}
          isCorrectingOdometer={isCorrectingOdometer}
          alertModalVisible={alertModalVisible}
          alertMessage={alertMessage}
          alertAction={alertAction}
          dismissAlertFlow={dismissAlertFlow}
          locationPermissionError={locationPermissionError}
          locationSettingsError={locationSettingsError}
          setLocationSettingsError={setLocationSettingsError}
          purchaseSuccessModal={purchaseSuccessModal}
          setPurchaseSuccessModal={setPurchaseSuccessModal}
          purchaseData={purchaseData}
          t={t}
        />
      );
    }

    // Show company modal if user has no employment.
    // WS7 (#207): a self-pay driver is entitled by their OWN license, so the
    // declared employment is informative — they must NEVER see the
    // "Adicionar Informações da Empresa" modal (that is the company-funded
    // onboarding path). Without this guard a freshly hydrated self-pay driver
    // (users/me -> employments: []) would render this modal over the dashboard
    // and `driver-dashboard` would never become visible (jobs 15920979476/79).
    if (shouldShowEmploymentModal({ hasActiveEmployment, employments, hasSelfPayLicense: hasSelfPayLicenseTriState }) && hasDriver) {
      // Check if user is a driver by checking if they are in the 'Driver' group
      return (
        <View style={styles.container}>
          <EmploymentModal
            visible={true}
            onDismiss={() => {}}
            fromPendingWarning={false}
            onBack={() => {}}
            form={companyForm}
            onSubmit={handleAddCompany}
            onLogout={handleLogout}
          />
        </View>
      );
    }

    // Show PendingWarningModal only after we have employment data and there are
    // pending employments — never to a driver who paid for their own license.
    if (!canOperateAsDriver && employments.length > 0) {
      return (
        <View  style={styles.container}>
          <PendingWarningModal
            visible={true}
            effectiveProfile={effectiveProfile}
            employments={employments}
            paymentIssue={paymentIssue}
            onFixPayment={paymentIssue ? fixSelfPayPayment : undefined}
            onSelfPay={goToSelfPay}
            onAddAnotherCompany={undefined}
            onSwitchProfile={hasOwner ? (newProfile: 'Owner' | 'Driver') => { updateProfile(newProfile); } : undefined}
            onLogout={handleLogout}
          />
        </View>
      );
    }

    // Show vehicle modal if the driver may operate but has no vehicles
    // Don't render anything if we're already redirecting to avoid render loops
    if (canOperateAsDriver && hasVehicles === false) {
      if (isRedirectingToOnboarding) {
        // Already redirecting, return null to avoid render loop
        return null;
      }
      
      if (!hasInitializedCache) {
        // Still initializing cache, show loading
        return (
          <View style={styles.container}>
            <LoadingScreen />
          </View>
        );
      }

      // A server-complete driver without a vehicle is already on the dashboard.
      // Show an explicit recovery state instead of a legacy loading screen that
      // claims to redirect forever. Vehicle setup is a post-active action.
      return (
        <>
          <DriverProfileRecoveryState
            desktopSidebarVisible={desktopSidebarVisible}
            width={width}
            handleLogout={handleLogout}
            hasOwner={hasOwner}
            selectedProfile={effectiveProfile}
            onSwitchProfile={hasOwner ? (newProfile: 'Owner' | 'Driver') => { updateProfile(newProfile); } : undefined}
            onOpenVehicleSetup={() => setVehicleModalVisible(true)}
          />
          <VehicleModal
            visible={vehicleModalVisible}
            onDismiss={() => setVehicleModalVisible(false)}
            form={vehicleForm}
            onSubmit={handleAddVehicle}
            localRegNumber={localRegNumber}
            setLocalRegNumber={setLocalRegNumber}
            formatRegistrationNumber={formatRegistrationNumber}
          />
        </>
      );
    }

    // Add early return for loading vehicles state only if the driver may operate and vehicles are still being fetched
    if (canOperateAsDriver && (hasVehicles === null || isLoading)) {
      
      // If timeout occurred, show error message
      if (initializationTimeout) {
        return (
          <View style={[styles.container, { padding: 24, justifyContent: 'center', alignItems: 'center' }]}>
            <Text style={{ fontSize: 18, fontWeight: 'bold', marginBottom: 16, color: theme.colors.error }}>
              {t('errors.errorLoadingData', 'Error loading data')}
            </Text>
            <Text style={{ textAlign: 'center', marginBottom: 24 }}>
              {t('errors.errorLoadingDataMessage', 'There was a problem loading your data. Please try again.')}
            </Text>
            <Button mode="contained" onPress={() => { void reloadApp(); }}>
              {t('errors.reloadPage', 'Reload Page')}
            </Button>
          </View>
        );
      }
      
      return (
        <LoadingScreen message={!hasInitializedCache ? t('loading.checkingData', 'Checking data...') : t('loading.loadingVehicles', 'Loading vehicles...')} />
      );
    }

    // If the driver may operate and has vehicles, show the main dashboard
    if (canOperateAsDriver && hasVehicles === true) {
      return (
        <View testID="driver-dashboard" style={styles.container}>
        {desktopSidebarVisible && (
            <Sidebar
              isOpen={true}
              onClose={() => {}} // Not needed for large screens
              screenWidth={width}
              onLogout={handleLogout}
              isOwner={hasOwner}
              isDriver={true}
              selectedProfile={effectiveProfile}
              onSwitchProfile={hasOwner ? (newProfile: 'Owner' | 'Driver') => { updateProfile(newProfile); } : undefined}
            />
          )}
          
          <Surface style={{ flex: 1, backgroundColor: theme.colors.background, ...(desktopSidebarVisible ? { marginLeft: SIDEBAR_WIDTH } : {}) }}>
            <DashboardContent
              location={location}
              trackingDegraded={trackingDegraded}
              isSessionActive={isSessionActive}
              isDriving={isDriving}
              drivingTimeToday={drivingTimeToday}
              drivingLimit={drivingLimit}
              drivingWeekLimits={drivingWeekLimits}
              stateSince={stateSince}
              actionInProgress={actionInProgress}
              onOpenSidebar={() => setSidebarOpen(true)}
              onStartSession={onStartSession}
              onEndSession={onEndSession}
              onStartDriving={onStartDriving}
              onStopDriving={onStopDriving}
              isStartingSession={isStartingSession}
              isEndingSession={isEndingSession}
              isStartingDriving={isStartingDriving}
              isStoppingDriving={isStoppingDriving}
              canEndSession={isDriving === false}
              onMovementAlert={showMovementAlert}
              onStationaryAlert={showStationaryAlert}
              // HOTFIX: phone verification disabled until migration is stable in production
              phoneVerified={true}
              phoneNumber={undefined}
              onVerifyPhonePress={undefined}
            />
          </Surface>

          {!isLargeScreen && (
            <Sidebar
              isOpen={sidebarOpen}
              onClose={() => setSidebarOpen(false)}
              screenWidth={width}
              onLogout={handleLogout}
              isOwner={hasOwner}
              isDriver={true}
              selectedProfile={effectiveProfile}
              onSwitchProfile={hasOwner ? (newProfile: 'Owner' | 'Driver') => { updateProfile(newProfile); } : undefined}
            />
          )}

          <EmploymentModal
            visible={employmentModalVisible}
            onDismiss={() => setModalVisible(false)}
            fromPendingWarning={fromPendingWarning}
            onBack={() => {
              setModalVisible(false);
              setFromPendingWarning(false);
            }}
            form={companyForm}
            onSubmit={handleAddCompany}
            onLogout={handleLogout}
          />
          <VehicleModal
            visible={vehicleModalVisible}
            onDismiss={() => setVehicleModalVisible(false)}
            form={vehicleForm}
            onSubmit={handleAddVehicle}
            localRegNumber={localRegNumber}
            setLocalRegNumber={setLocalRegNumber}
            formatRegistrationNumber={formatRegistrationNumber}
          />
          <SessionModal
            visible={startSessionModalVisible}
            onDismiss={() => setStartSessionModalVisible(false)}
            form={startOdometerForm}
            onSubmit={handleStartSessionSubmit}
            onSubmitWithLocation={handleStartSessionWithProvidedLocationWrapper}
            selectedVehicle={selectedVehicle}
            vehicles={vehicles}
            onSelectVehicle={setSelectedVehicle}
            allowVehicleSelection={true}
            title="startSession"
            buttonText="startSession"
            isLoading={isStartingSession}
            latestOdometer={latestOdometer}
            retryLocationPermission={retryLocationPermission}
          />
          <SessionModal
            visible={endSessionModalVisible}
            onDismiss={() => setEndSessionModalVisible(false)}
            form={endOdometerForm}
            onSubmit={handleEndSessionSubmit}
            onSubmitWithLocation={handleEndSessionSubmitWithProvidedLocation}
            selectedVehicle={selectedVehicle}
            vehicles={vehicles}
            onSelectVehicle={setSelectedVehicle}
            allowVehicleSelection={false}
            title="endSession"
            buttonText="endSession"
            isLoading={isEndingSession}
            latestOdometer={latestOdometer}
            retryLocationPermission={retryLocationPermission}
          />
          {velocityWarningData && (
            <OdometerVelocityModal
              visible={velocityWarningVisible}
              onDismiss={() => {
                setVelocityWarningVisible(false);
                setVelocityWarningData(null);
                setPendingSessionData(null);
                setPendingLocationWkt(null);
                setVelocityWarningContext(null);
                // Reopen session modal only if warning came from start-session
                if (velocityWarningContext === 'start') {
                  setStartSessionModalVisible(true);
                }
                if (velocityWarningContext === 'end') {
                  setEndSessionModalVisible(true);
                }
              }}
              onConfirm={handleVelocityWarningConfirm}
              meanVelocityKmh={velocityWarningData.mean_velocity_kmh}
              previousOdometer={velocityWarningData.previous_odometer}
              newOdometer={pendingSessionData?.odometer || 0}
              timeDifferenceHours={velocityWarningData.time_difference_hours}
              isLoading={velocityWarningContext === 'end' ? (isEndingSession || isVelocityConfirming) : isStartingSession}
            />
          )}
          {previousSessionOut && newOdometerValue !== null && (
            <OdometerCorrectionModal
              visible={correctionModalVisible}
              onDismiss={() => {
                const isEnd = correctionContext === 'end';
                setCorrectionModalVisible(false);
                setCorrectionContext(null);
                setPreviousSessionOut(null);
                setNewOdometerValue(null);
                setPendingSessionData(null);
                if (isEnd) {
                  setEndSessionModalVisible(true);
                } else {
                  setStartSessionModalVisible(true);
                }
              }}
              onConfirm={handleOdometerCorrection}
              previousSessionOut={previousSessionOut}
              newOdometer={newOdometerValue}
              isLoading={isCorrectingOdometer}
            />
          )}
          <PendingWarningModal
            visible={!canOperateAsDriver && employments.length > 0}
            employments={employments}
            paymentIssue={paymentIssue}
            onFixPayment={paymentIssue ? fixSelfPayPayment : undefined}
            onSelfPay={goToSelfPay}
            onAddAnotherCompany={() => {
              setModalVisible(true);
              setFromPendingWarning(true);
            }}
            onSwitchProfile={hasOwner ? (newProfile: 'Owner' | 'Driver') => { updateProfile(newProfile); } : undefined}
            onLogout={handleLogout}
          />
          {alertPortal}
          <Portal>
            <Dialog visible={locationPermissionError} dismissable={false} style={Platform.OS === 'web' ? {
              maxWidth: 600,
              width: '90%',
              alignSelf: 'center',
            } : {}}>
              <Dialog.Title>{t('alerts.locationPermissionRequired', 'Location Permission Required')}</Dialog.Title>
              <Dialog.Content>
                <Text variant="bodyMedium">
                  {t('alerts.locationPermissionRequiredMessage', 'The app needs access to your location to function properly. Please grant location permission.')}
                </Text>
                <Text variant="bodyMedium" style={{ marginTop: 12, color: theme.colors.error }}>
                  {t('alerts.locationPermissionRetryMessage', 'If you just changed location permissions in your browser settings, please refresh the page to continue.')}
                </Text>
              </Dialog.Content>
              <Dialog.Actions>
                <Button onPress={retryLocationPermission}>{t('alerts.tryAgain', 'Try Again')}</Button>
                <Button onPress={() => { void reloadApp(); }}>{t('alerts.refreshPage', 'Refresh Page')}</Button>
              </Dialog.Actions>
            </Dialog>
          </Portal>

          {/* Location settings (GPS) error dialog */}
          <Portal>
            <Dialog
              visible={locationSettingsError}
              onDismiss={() => setLocationSettingsError(false)}
              dismissable={!!(Platform.OS === 'web')}
              style={Platform.OS === 'web' ? { maxWidth: 600, width: '90%', alignSelf: 'center' } : {}}
            >
              <Dialog.Title>{t('alerts.locationSettingsInvalid', 'Location Unavailable')}</Dialog.Title>
              <Dialog.Content>
                <Text variant="bodyMedium">
                  {t('alerts.locationSettingsInvalidMessage', 'Could not get your location. Please make sure GPS is enabled and location mode is set to high accuracy.')}
                </Text>
              </Dialog.Content>
              <Dialog.Actions>
                {Platform.OS !== 'web' && (
                  <Button onPress={() => Linking.openSettings()}>
                    {t('alerts.openDeviceSettings', 'Open Settings')}
                  </Button>
                )}
                <Button onPress={() => setLocationSettingsError(false)}>
                  {t('alerts.tryAgain', 'Try Again')}
                </Button>
              </Dialog.Actions>
            </Dialog>
          </Portal>

          {/* Purchase Success Modal */}
          <Portal>
            <Dialog 
              visible={purchaseSuccessModal} 
              onDismiss={() => setPurchaseSuccessModal(false)}
              style={Platform.OS === 'web' ? {
                maxWidth: 500,
                width: '90%',
                alignSelf: 'center',
              } : {}}
            >
              <Dialog.Title style={{ color: theme.colors.primary }}>
                {t('purchase.success.title', '🎉 Purchase Successful!')}
              </Dialog.Title>
              <Dialog.Content>
                <Text variant="bodyLarge" style={{ marginBottom: 16 }}>
                  {purchaseData?.isNew 
                    ? t('purchase.success.messageNew', 'Your GPS licenses have been successfully purchased!')
                    : t('purchase.success.messageUpdated', 'Your GPS licenses have been successfully updated!')
                  }
                </Text>
                <View style={{ 
                  backgroundColor: theme.colors.primaryContainer, 
                  padding: 16, 
                  borderRadius: 8,
                  marginTop: 8 
                }}>
                  <Text variant="titleMedium" style={{ fontWeight: 'bold', marginBottom: 8 }}>
                    {t('purchase.success.licenses', 'Licenses Purchased:')}
                  </Text>
                  <Text variant="headlineMedium" style={{ 
                    color: theme.colors.primary, 
                    fontWeight: 'bold',
                    textAlign: 'center',
                    marginTop: 4
                  }}>
                    {purchaseData?.licenses} {t('purchase.success.gpsLicenses', 'GPS Licenses')}
                  </Text>
                </View>
                <Text variant="bodyMedium" style={{ marginTop: 16, opacity: 0.7 }}>
                  {t('purchase.success.ready', 'You can now assign these licenses to your drivers and vehicles.')}
                </Text>
              </Dialog.Content>
              <Dialog.Actions>
                <Button onPress={() => setPurchaseSuccessModal(false)}>
                  {t('purchase.success.close', 'Got it!')}
                </Button>
              </Dialog.Actions>
            </Dialog>
          </Portal>
        </View>
      );
    }
  }

  // Fallback UI if no profile matches
  return (
    <View  style={styles.container}>
      <Text>Carregando...</Text>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    flexDirection: 'row',
  },
  contentContainer: {
    flex: 1,
    padding: 20,
  },
  contentWithSidebar: {
    marginLeft: 240, // Width of the sidebar
  },
  card: {
    marginBottom: 20,
    elevation: 4,
  },
  companyHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: 24,
  },
  companyHeaderMobile: {
    flexDirection: 'column',
    gap: 16,
  },
  companyInfo: {
    flex: 1,
  },
  companyDetails: {
    gap: 4,
  },
  metricsContainer: {
    flexDirection: 'row',
    borderRadius: 12,
    padding: 16,
    gap: 16,
  },
  metricsContainerMobile: {
    flexDirection: 'column',
    width: '100%',
    gap: 12,
  },
  metricItem: {
    alignItems: 'center',
    minWidth: 100,
  },
  metricDivider: {
    width: 1,
    height: '100%',
  },
  actionButtonsContainer: {
    gap: 12,
    marginTop: 24,
  },
  actionButtonsContainerDesktop: {
    flexDirection: 'row',
    gap: 16,
  },
  actionCard: {
    elevation: 1,
  },
  actionCardDesktop: {
    flex: 1,
  },
  actionCardContent: {
    padding: 16,
  },
  actionRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  actionInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  actionTextContainer: {
    marginLeft: 12,
    flex: 1,
  },
  actionButton: {
    marginLeft: 16,
    minWidth: 80,
  },
  actionButtonLabel: {
    fontSize: 14,
  },
  statsContainer: {
    marginTop: 32,
  },
  metricsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 16,
    marginBottom: 24,
  },
  metricsGridMobile: {
    flexDirection: 'column',
    gap: 12,
  },
  metricCard: {
    flex: 1,
    minWidth: 200,
    elevation: 2,
  },
  metricCardMobile: {
    width: '100%',
    minWidth: 'auto',
  },
  metricCardContent: {
    padding: 16,
  },
  metricHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
    justifyContent: 'center',
  },
  statusChip: {
    alignSelf: 'flex-start',
    marginTop: 8,
  },
  mapCard: {
    marginTop: 16,
    elevation: 2,
    marginBottom: 24,
  },
  mapContainer: {
    height: 400,
    borderRadius: 8,
    overflow: 'hidden',
  },
  mapContainerMobile: {
    height: 300,
  },
  map: {
    width: '100%',
    height: '100%',
  },
});
